import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../lib/api";
import type { Card, PartnerRedemptionRow, Person, PointsTransfer, TransferPartner } from "../lib/types";
import { formatDate, formatINR, formatPoints, todayInput } from "../lib/format";
import { EmptyState, ErrorBanner, Modal, Panel, Spinner } from "./ui";

/** Logging a transfer and the history of the ones already made. */
export function TransferLog({
  onError,
  onNotice,
}: {
  onError: (m: string) => void;
  onNotice: (m: string) => void;
}) {
  const [transfers, setTransfers] = useState<PointsTransfer[] | null>(null);
  const [redemptions, setRedemptions] = useState<PartnerRedemptionRow[]>([]);
  const [partners, setPartners] = useState<TransferPartner[]>([]);
  const [cards, setCards] = useState<Card[]>([]);
  const [open, setOpen] = useState(false);
  const [redeemOpen, setRedeemOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [t, r, p, c] = await Promise.all([
        api.get<{ transfers: PointsTransfer[] }>("/transfers"),
        api.get<{ redemptions: PartnerRedemptionRow[] }>("/transfers/redemptions"),
        api.get<{ partners: TransferPartner[] }>("/transfers/partners"),
        api.get<{ cards: Card[] }>("/cards"),
      ]);
      setTransfers(t.transfers);
      setRedemptions(r.redemptions);
      setPartners(p.partners.filter((x) => x.isActive));
      setCards(c.cards.filter((x) => x.isActive));
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Could not load transfers");
    }
  }, [onError]);

  useEffect(() => { void load(); }, [load]);

  async function removeRedemption(r: PartnerRedemptionRow) {
    if (!confirm(`Delete "${r.description}"? ${formatPoints(r.unitsUsed)} miles go back to ${r.partner.name}.`)) return;
    try {
      await api.del(`/transfers/redemptions/${r.id}`);
      onNotice("Redemption removed.");
      await load();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Could not delete the redemption");
    }
  }

  async function remove(t: PointsTransfer) {
    if (!confirm(`Delete this transfer? ${formatPoints(t.unitsReceived)} miles will be taken back off ${t.partner.name}.`)) return;
    try {
      await api.del(`/transfers/${t.id}`);
      onNotice("Transfer removed — the points are back on the card and the miles are off the partner.");
      await load();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Could not delete the transfer");
    }
  }

  if (!transfers) return <Spinner />;

  const totalPoints = transfers.reduce((s, t) => s + t.pointsUsed, 0);
  const totalUnits = transfers.reduce((s, t) => s + t.unitsReceived, 0);

  return (
    <>
      <Panel
        title="Transfers made"
        subtitle={
          transfers.length > 0
            ? `${formatPoints(totalPoints)} points moved into ${formatPoints(totalUnits)} miles`
            : undefined
        }
        actions={
          <button className="btn-primary px-3 py-1.5 text-xs" onClick={() => setOpen(true)} disabled={partners.length === 0}>
            Log a transfer
          </button>
        }
      >
        {transfers.length === 0 ? (
          <EmptyState
            title="Nothing transferred yet"
            hint="Logging a transfer takes the points off the card, counts them against its caps, and adds the miles to the partner."
            action={
              <button className="btn-primary" onClick={() => setOpen(true)} disabled={partners.length === 0}>
                Log a transfer
              </button>
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-ink-200">
                  <th className="th">Date</th>
                  <th className="th">From</th>
                  <th className="th">To</th>
                  <th className="th text-right">Points</th>
                  <th className="th text-right">Miles</th>
                  <th className="th"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {transfers.map((t) => (
                  <tr key={t.id}>
                    <td className="td whitespace-nowrap text-ink-500">{formatDate(t.date)}</td>
                    <td className="td">
                      <span className="inline-flex items-center gap-2">
                        <span
                          className="h-2.5 w-2.5 shrink-0 rounded-sm"
                          style={{ background: t.card?.color ?? "#647694" }}
                          aria-hidden
                        />
                        <span>
                          <span className="font-medium text-ink-900">
                            {t.card?.name ?? t.fromPartner?.name ?? "—"}
                          </span>
                          <span className="block text-xs text-ink-500">
                            {t.card ? t.card.currency?.name ?? "—" : "Miles already held"}
                          </span>
                        </span>
                      </span>
                    </td>
                    <td className="td">
                      <span className="text-ink-800">{t.partner.name}</span>
                      {t.notes && <span className="block text-xs text-ink-500">{t.notes}</span>}
                    </td>
                    <td className="td tnum text-right">{formatPoints(t.pointsUsed)}</td>
                    <td className="td tnum text-right font-medium">
                      {formatPoints(t.unitsReceived)}
                      {t.bonusUnits > 0 && (
                        <span className="block text-xs text-emerald-700">
                          incl. {formatPoints(t.bonusUnits)} bonus
                        </span>
                      )}
                    </td>
                    <td className="td text-right">
                      <button className="text-xs text-red-700 underline" onClick={() => remove(t)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Panel
        className="mt-6"
        title="Miles redeemed"
        subtitle={
          redemptions.length > 0
            ? `${formatPoints(redemptions.reduce((a, r) => a + r.unitsUsed, 0))} miles spent for ${formatINR(
                redemptions.reduce((a, r) => a + r.valueReceived, 0),
              )} of travel`
            : undefined
        }
        actions={
          <button
            className="btn-secondary px-3 py-1.5 text-xs"
            onClick={() => setRedeemOpen(true)}
            disabled={partners.length === 0}
          >
            Log a redemption
          </button>
        }
      >
        {redemptions.length === 0 ? (
          <EmptyState
            title="No redemptions logged"
            hint="Record the flight or the hotel night, and the app works out what you actually got per mile."
            action={
              <button className="btn-primary" onClick={() => setRedeemOpen(true)} disabled={partners.length === 0}>
                Log a redemption
              </button>
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-ink-200">
                  <th className="th">Date</th>
                  <th className="th">What you booked</th>
                  <th className="th">Programme</th>
                  <th className="th text-right">Miles</th>
                  <th className="th text-right">Worth</th>
                  <th className="th text-right">Per mile</th>
                  <th className="th"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {redemptions.map((r) => {
                  const good = r.valuePerUnit >= r.partner.valuePerUnit;
                  return (
                    <tr key={r.id}>
                      <td className="td whitespace-nowrap text-ink-500">{formatDate(r.date)}</td>
                      <td className="td">
                        <span className="font-medium text-ink-900">{r.description}</span>
                        <span className="block text-xs text-ink-500">
                          {r.type.charAt(0) + r.type.slice(1).toLowerCase()}
                          {r.forPerson && ` · for ${r.forPerson.name}`}
                          {r.cashPaid > 0 && ` · plus ${formatINR(r.cashPaid)} cash`}
                        </span>
                      </td>
                      <td className="td text-ink-700">{r.partner.name}</td>
                      <td className="td tnum text-right">{formatPoints(r.unitsUsed)}</td>
                      <td className="td tnum text-right">{formatINR(r.valueReceived)}</td>
                      <td className={`td tnum text-right font-medium ${good ? "text-emerald-700" : "text-amber-700"}`}>
                        ₹{r.valuePerUnit.toFixed(2)}
                      </td>
                      <td className="td text-right">
                        <button className="text-xs text-red-700 underline" onClick={() => removeRedemption(r)}>
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="mt-3 text-xs text-ink-500">
              Green means you beat the value you set for that programme; amber means you did worse.
            </p>
          </div>
        )}
      </Panel>

      <RedeemModal
        open={redeemOpen}
        partners={partners}
        onClose={() => setRedeemOpen(false)}
        onSaved={() => { setRedeemOpen(false); onNotice("Redemption logged."); void load(); }}
      />

      <LogModal
        open={open}
        cards={cards}
        partners={partners}
        onClose={() => setOpen(false)}
        onSaved={() => { setOpen(false); onNotice("Transfer logged."); void load(); }}
      />
    </>
  );
}

function LogModal({
  open,
  cards,
  partners,
  onClose,
  onSaved,
}: {
  open: boolean;
  cards: Card[];
  partners: TransferPartner[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    sourceKind: "CARD" as "CARD" | "PARTNER",
    cardId: "",
    fromPartnerId: "",
    partnerId: "",
    date: todayInput(),
    pointsUsed: "",
    unitsReceived: "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setForm({
      sourceKind: "CARD",
      cardId: cards[0]?.id ?? "",
      fromPartnerId: partners[0]?.id ?? "",
      partnerId: partners[0]?.id ?? "",
      date: todayInput(),
      pointsUsed: "",
      unitsReceived: "",
      notes: "",
    });
    setError(null);
  }, [open, cards, partners]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post("/transfers", {
        ...(form.sourceKind === "CARD" ? { cardId: form.cardId } : { fromPartnerId: form.fromPartnerId }),
        partnerId: form.partnerId,
        date: form.date,
        pointsUsed: Number(form.pointsUsed),
        // Left blank, the route's ratio works it out.
        ...(form.unitsReceived.trim() === "" ? {} : { unitsReceived: Number(form.unitsReceived) }),
        notes: form.notes.trim() || null,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not log the transfer");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Log a transfer">
      <form onSubmit={save} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="label" htmlFor="t-kind">Points come from</label>
            <select
              id="t-kind"
              className="input"
              value={form.sourceKind}
              onChange={(e) => setForm({ ...form, sourceKind: e.target.value as "CARD" | "PARTNER" })}
            >
              <option value="CARD">A card's reward programme</option>
              <option value="PARTNER">Miles I already hold somewhere</option>
            </select>
          </div>
          {form.sourceKind === "CARD" ? (
            <div>
              <label className="label" htmlFor="t-card">From card</label>
              <select id="t-card" className="input" required value={form.cardId} onChange={(e) => setForm({ ...form, cardId: e.target.value })}>
                {cards.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          ) : (
            <div>
              <label className="label" htmlFor="t-from">From programme</label>
              <select id="t-from" className="input" required value={form.fromPartnerId} onChange={(e) => setForm({ ...form, fromPartnerId: e.target.value })}>
                {partners.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="label" htmlFor="t-partner">To programme</label>
            <select id="t-partner" className="input" required value={form.partnerId} onChange={(e) => setForm({ ...form, partnerId: e.target.value })}>
              {partners.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label" htmlFor="t-date">Date</label>
            <input id="t-date" className="input" type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          </div>
          <div>
            <label className="label" htmlFor="t-points">Points sent</label>
            <input id="t-points" className="input tnum" type="number" min={1} required value={form.pointsUsed} onChange={(e) => setForm({ ...form, pointsUsed: e.target.value })} />
          </div>
        </div>

        <div>
          <label className="label" htmlFor="t-units">Miles received (optional)</label>
          <input
            id="t-units"
            className="input tnum"
            type="number"
            min={0}
            value={form.unitsReceived}
            onChange={(e) => setForm({ ...form, unitsReceived: e.target.value })}
            placeholder="Leave blank to use the route's ratio"
          />
          <p className="mt-1 text-xs text-ink-500">
            Fill this in when a bonus landed you more than the standard ratio.
          </p>
        </div>

        <div>
          <label className="label" htmlFor="t-notes">Notes</label>
          <input id="t-notes" className="input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </div>

        <p className="rounded-lg border border-ink-200 bg-ink-50 px-3 py-2 text-xs text-ink-600">
          This takes the points off the source balance, counts them against that card's transfer caps where one
          applies, and adds the miles to the destination. Any transfer bonus on the route is worked out for you.
        </p>

        {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={busy}>{busy ? "Saving…" : "Log transfer"}</button>
        </div>
      </form>
    </Modal>
  );
}


/* ------------------------------------------------------- redeeming miles */

function RedeemModal({
  open,
  partners,
  onClose,
  onSaved,
}: {
  open: boolean;
  partners: TransferPartner[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    partnerId: "",
    date: todayInput(),
    unitsUsed: "",
    valueReceived: "",
    cashPaid: "",
    type: "FLIGHT",
    description: "",
    notes: "",
    forPersonId: "",
  });
  const [people, setPeople] = useState<Person[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setForm({
      partnerId: partners[0]?.id ?? "",
      date: todayInput(),
      unitsUsed: "",
      valueReceived: "",
      cashPaid: "",
      type: "FLIGHT",
      description: "",
      notes: "",
      forPersonId: "",
    });
    void api.get<{ people: Person[] }>("/people").then((r) => setPeople(r.people)).catch(() => setPeople([]));
    setError(null);
  }, [open, partners]);

  const units = Number(form.unitsUsed);
  const value = Number(form.valueReceived);
  const perUnit = units > 0 && Number.isFinite(value) ? value / units : null;
  const held = partners.find((p) => p.id === form.partnerId)?.balance?.balanceUnits ?? 0;

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post("/transfers/redemptions", {
        partnerId: form.partnerId,
        date: form.date,
        unitsUsed: units,
        valueReceived: Number(form.valueReceived) || 0,
        cashPaid: Number(form.cashPaid) || 0,
        type: form.type,
        description: form.description.trim(),
        notes: form.notes.trim() || null,
        forPersonId: form.forPersonId || null,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not log the redemption");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Log a redemption">
      <form onSubmit={save} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="label" htmlFor="rd-partner">Programme</label>
            <select id="rd-partner" className="input" required value={form.partnerId} onChange={(e) => setForm({ ...form, partnerId: e.target.value })}>
              {partners.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} — {formatPoints(p.balance?.balanceUnits ?? 0)} held
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label" htmlFor="rd-type">What for</label>
            <select id="rd-type" className="input" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
              <option value="FLIGHT">Flight</option>
              <option value="HOTEL">Hotel</option>
              <option value="UPGRADE">Upgrade</option>
              <option value="OTHER">Other</option>
            </select>
          </div>
          <div>
            <label className="label" htmlFor="rd-date">Date</label>
            <input id="rd-date" className="input" type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          </div>
          <div>
            <label className="label" htmlFor="rd-units">Miles used</label>
            <input id="rd-units" className="input tnum" type="number" min={1} required value={form.unitsUsed} onChange={(e) => setForm({ ...form, unitsUsed: e.target.value })} />
            <p className="mt-1 text-xs text-ink-500">{formatPoints(held)} available</p>
          </div>
          <div>
            <label className="label" htmlFor="rd-value">Cash price it replaced (₹)</label>
            <input id="rd-value" className="input tnum" type="number" min={0} value={form.valueReceived} onChange={(e) => setForm({ ...form, valueReceived: e.target.value })} />
          </div>
          <div>
            <label className="label" htmlFor="rd-cash">Cash you still paid (₹)</label>
            <input id="rd-cash" className="input tnum" type="number" min={0} value={form.cashPaid} onChange={(e) => setForm({ ...form, cashPaid: e.target.value })} placeholder="Taxes and surcharges" />
          </div>
        </div>

        <div>
          <label className="label" htmlFor="rd-desc">What you booked</label>
          <input id="rd-desc" className="input" required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g. Delhi–Singapore, Business saver" />
        </div>

        {people.length > 1 && (
          <div>
            <label className="label" htmlFor="rd-for">Who it was for</label>
            <select
              id="rd-for"
              className="input"
              value={form.forPersonId}
              onChange={(e) => setForm({ ...form, forPersonId: e.target.value })}
            >
              <option value="">Not recorded</option>
              {people.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-ink-500">
              Pooling points is what lets them be spent on whoever is travelling.
            </p>
          </div>
        )}

        <div>
          <label className="label" htmlFor="rd-notes">Notes</label>
          <input id="rd-notes" className="input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </div>

        {perUnit != null && (
          <p className="rounded-lg border border-ink-200 bg-ink-50 px-3 py-2 text-sm text-ink-700">
            That is <strong className="tnum">₹{perUnit.toFixed(2)}</strong> per mile.
          </p>
        )}

        {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={busy}>{busy ? "Saving…" : "Log redemption"}</button>
        </div>
      </form>
    </Modal>
  );
}
